import React, { useState, useEffect } from 'react';
import { DB } from '../data/database';
import { formatPrice, getJpyRate, calculateContainerTotalJPY, convertCurrency } from '../utils/currency';
import {
  CarIcon, EngineIcon, PartsIcon, ContainerIcon,
  CheckIcon, CalculatorIcon, DollarSignIcon, PackageIcon
} from './Icons';
import StatCard from './ui/StatCard';

// ─── FOB Research Notes (displayed in UI) ────────────────────
// Standard Japan automotive export FOB breakdown:
//  Unit costs:    purchase price + auction fee (~5%) + domestic transport
//                 + inspection/de-pollution + export documentation
//  Container costs: ocean freight + drayage (truck to port)
//                 + terminal handling (THC) + bill of lading + loading/lashing
//  Final FOB = unit subtotal + (container total ÷ units in container) + margin

const CONTAINER_FIELDS = [
  { key: 'freight',      label: 'Ocean Freight',      hint: 'Total sea freight for this container' },
  { key: 'drayage',      label: 'Drayage',            hint: 'Truck from yard/depot to port terminal' },
  { key: 'port_charges', label: 'Port / THC',         hint: 'Terminal handling charge at origin port' },
  { key: 'bl_fee',       label: 'Bill of Lading',     hint: 'B/L issuance fee' },
  { key: 'loading_fee',  label: 'Loading & Lashing',  hint: 'Cost to load and secure units in container' },
];

// ─── Container Cost Card ──────────────────────────────────────
function ContainerCostCard({ container, currency, setPage }) {
  const [open, setOpen]   = useState(false);
  const costs = container.costs || { fixed: {}, custom: [] };

  const unitCount  = container.units?.length || 1;
  const totalCost  = calculateContainerTotalJPY(costs);
  const perUnit    = unitCount > 0 ? Math.round(totalCost / unitCount) : 0;
  const hasCosting = totalCost > 0;

  return (
    <div className={`pricing-card${open ? ' open' : ''}`}>
      <div className="pricing-card-header" onClick={() => setOpen(o => !o)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div className="pricing-card-icon"><ContainerIcon /></div>
          <div>
            <div className="pricing-card-title">{container.number}</div>
            <div className="pricing-card-sub">
              {container.origin} → {container.destination}
              &nbsp;·&nbsp;{unitCount} unit{unitCount !== 1 ? 's' : ''}
              {container.eta ? ` · ETA ${container.eta}` : ''}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          {hasCosting ? (
            <div style={{ textAlign: 'right' }}>
              <div className="pricing-meta-lbl">Per-unit share</div>
              <div className="pricing-meta-val green">{formatPrice(perUnit, currency)}</div>
            </div>
          ) : (
            <span className="pricing-no-cost">No costs set</span>
          )}
          <span className="pricing-chevron">{open ? '▲' : '▼'}</span>
        </div>
      </div>

      {open && (
        <div className="pricing-card-body">
          <button
            onClick={() => {
              if (setPage) {
                sessionStorage.setItem('bs_open_container_costs', container.id);
                setPage('containers');
              }
            }}
            style={{ fontSize: 11, color: 'var(--blue)', padding: '8px 12px', background: 'var(--bg3)', borderRadius: 8, marginBottom: 12, border: '1px solid var(--border)', cursor: 'pointer', width: '100%', textAlign: 'left', transition: 'background 0.15s' }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg2)'}
            onMouseLeave={e => e.currentTarget.style.background = 'var(--bg3)'}
          >
            💰 Edit costs in Container Detail → Costs tab →
          </button>
          <div className="pricing-summary-box">
            {CONTAINER_FIELDS.map(({ key, label }) => {
              const entry = costs.fixed?.[key];
              const amt = Number(entry?.amount) || 0;
              const cur = entry?.currency || 'JPY';
              const jpyAmt = convertCurrency(amt, cur, 'JPY');
              return (
                <div key={key} className="pricing-sum-row">
                  <span>{label}</span>
                  <span>
                    {amt > 0 ? (
                      <>
                        {cur !== 'JPY' && <span style={{ color: 'var(--text3)', marginRight: 6 }}>{cur} {amt.toLocaleString()}</span>}
                        {formatPrice(jpyAmt, currency)}
                      </>
                    ) : (
                      <span style={{ color: 'var(--text3)' }}>--</span>
                    )}
                  </span>
                </div>
              );
            })}
            {(costs.custom || []).length > 0 && (
              <>
                <div className="pricing-sum-divider" />
                {costs.custom.map((item, i) => {
                  const lineTotal = (Number(item.quantity) || 1) * (Number(item.unit_cost) || 0);
                  const jpyAmt = convertCurrency(lineTotal, item.currency || 'JPY', 'JPY');
                  return (
                    <div key={i} className="pricing-sum-row">
                      <span>{item.description || 'Custom item'}</span>
                      <span>{formatPrice(jpyAmt, currency)}</span>
                    </div>
                  );
                })}
              </>
            )}
            <div className="pricing-sum-divider" />
            <div className="pricing-sum-row bold">
              <span>Total Container Cost</span>
              <span>{formatPrice(totalCost, currency)}</span>
            </div>
            <div className="pricing-sum-row highlight">
              <span>Per-Unit Share ({unitCount} units)</span>
              <span>{formatPrice(perUnit, currency)}</span>
            </div>
            <div className="pricing-sum-hint">
              This amount is added to each unit's FOB price automatically.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Stable FixedField (outside any component to prevent re-mount) ───
const FixedField = React.memo(function FixedField({ fieldKey, label, hint, placeholder, costs, setCosts }) {
  return (
    <div className="pricing-field-row">
      <div className="pricing-field-meta">
        <div className="pricing-field-label">{label}</div>
        <div className="pricing-field-hint">{hint}</div>
      </div>
      <div className="pricing-input-wrap">
        <span className="pricing-currency">¥</span>
        <input
          type="number" className="pricing-input" min="0"
          value={costs[fieldKey]}
          onChange={e => setCosts(prev => ({ ...prev, [fieldKey]: e.target.value }))}
          placeholder={placeholder}
        />
      </div>
    </div>
  );
});

// ─── Unit Pricing Row ─────────────────────────────────────────
// Flexible field modes: 'fixed' = JPY input, 'pct' = percentage input
function ModeToggle({ mode, onChange }) {
  return (
    <div className="pricing-mode-toggle">
      <button
        type="button"
        className={`pricing-mode-btn${mode === 'fixed' ? ' active' : ''}`}
        onClick={() => onChange('fixed')}
        title="Fixed amount (¥)"
      >¥</button>
      <button
        type="button"
        className={`pricing-mode-btn${mode === 'pct' ? ' active' : ''}`}
        onClick={() => onChange('pct')}
        title="Percentage (%)"
      >%</button>
    </div>
  );
}

function UnitPricingRow({ item, itemType, containerShare, containerName, currency, onSave }) {
  const [open, setOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [contShare, setContShare] = useState(containerShare || item.containerShare || 0);
  const [customCharges, setCustomCharges] = useState(() => {
    const existing = Array.isArray(item.customCharges) ? item.customCharges : [];
    // Migrate old single custom charge
    if (existing.length === 0 && item.customCharge) {
      return [{ label: item.customChargeLabel || 'Custom', amount: item.customCharge }];
    }
    return existing.length > 0 ? existing : [];
  });
  const [costs, setCosts] = useState({
    purchasePrice:     item.purchasePrice     || 0,
    auctionFee:        item.auctionFee        || 0,
    domesticTransport: item.domesticTransport || 0,
    inspectionFee:     item.inspectionFee     || 0,
    exportDocs:        item.exportDocs        || 0,
    marginAmount:      item.marginAmount      || 0,
  });

  // Percentage modes — derived from saved data if possible
  const initAuctionPct = () => {
    if ((item.purchasePrice || 0) > 0 && (item.auctionFee || 0) > 0)
      return +((item.auctionFee / item.purchasePrice) * 100).toFixed(2);
    return 5;
  };
  const initMarginPct = () => {
    const base = (item.purchasePrice || 0) + (item.auctionFee || 0) +
      (item.domesticTransport || 0) + (item.inspectionFee || 0) +
      (item.exportDocs || 0) + (containerShare || 0);
    if (base > 0 && (item.marginAmount || 0) > 0)
      return +((item.marginAmount / base) * 100).toFixed(2);
    return 10;
  };

  const [auctionMode, setAuctionMode] = useState('fixed');
  const [auctionPct,  setAuctionPct]  = useState(initAuctionPct);
  const [marginMode,  setMarginMode]  = useState('fixed');
  const [marginPct,   setMarginPct]   = useState(initMarginPct);

  // Always-JPY setter (for plain fields)
  const set = (k, v) => setCosts(prev => ({ ...prev, [k]: v }));

  // ── Auction fee helpers ──
  const handleAuctionModeChange = (mode) => {
    setAuctionMode(mode);
    if (mode === 'pct') {
      // Derive pct from current values if meaningful
      const pp = Number(costs.purchasePrice);
      if (pp > 0 && Number(costs.auctionFee) > 0)
        setAuctionPct(+((Number(costs.auctionFee) / pp) * 100).toFixed(2));
      // Recalculate JPY from current pct
      setCosts(prev => ({
        ...prev,
        auctionFee: Math.round(Number(prev.purchasePrice) * auctionPct / 100),
      }));
    }
  };

  const handleAuctionPctChange = (pct) => {
    setAuctionPct(pct);
    setCosts(prev => ({
      ...prev,
      auctionFee: Math.round(Number(prev.purchasePrice) * Number(pct) / 100),
    }));
  };

  // When purchase price changes in pct mode, keep auction fee in sync
  const handlePurchasePriceChange = (v) => {
    const pp = Number(v);
    setCosts(prev => ({
      ...prev,
      purchasePrice: v,
      ...(auctionMode === 'pct'
        ? { auctionFee: Math.round(pp * auctionPct / 100) }
        : {}),
    }));
  };

  // ── Margin helpers ──
  // Margin % is calculated on total COST (subtotal + container share) — standard markup
  const getCostBase = (c) =>
    (Number(c.purchasePrice) || 0) +
    (Number(c.auctionFee)    || 0) +
    (Number(c.domesticTransport) || 0) +
    (Number(c.inspectionFee) || 0) +
    (Number(c.exportDocs)    || 0) +
    (containerShare          || 0);

  const handleMarginModeChange = (mode) => {
    setMarginMode(mode);
    if (mode === 'pct') {
      const base = getCostBase(costs);
      if (base > 0 && Number(costs.marginAmount) > 0)
        setMarginPct(+((Number(costs.marginAmount) / base) * 100).toFixed(2));
      setCosts(prev => ({
        ...prev,
        marginAmount: Math.round(getCostBase(prev) * marginPct / 100),
      }));
    }
  };

  const handleMarginPctChange = (pct) => {
    setMarginPct(pct);
    setCosts(prev => ({
      ...prev,
      marginAmount: Math.round(getCostBase(prev) * Number(pct) / 100),
    }));
  };

  // ── Calculations ──
  const unitSubtotal =
    (Number(costs.purchasePrice)     || 0) +
    (Number(costs.auctionFee)        || 0) +
    (Number(costs.domesticTransport) || 0) +
    (Number(costs.inspectionFee)     || 0) +
    (Number(costs.exportDocs)        || 0);

  const customTotal  = customCharges.reduce((s, c) => s + (Number(c.amount) || 0), 0);
  const fob          = unitSubtotal + (Number(contShare) || 0) + customTotal + (Number(costs.marginAmount) || 0);
  const costBase     = unitSubtotal + (Number(contShare) || 0) + customTotal;
  const marginOnCost = costBase > 0 ? ((Number(costs.marginAmount) / costBase) * 100).toFixed(1) : '0';
  const marginOnFOB  = fob > 0    ? ((Number(costs.marginAmount) / fob)       * 100).toFixed(1) : '0';
  const priceChanged = Math.round(fob) !== (item.priceJPY || 0);

  const name     = item.make ? `${item.make} ${item.model}` : item.engineName || item.partName || 'Unknown';
  const ItemIcon = itemType === 'vehicles' ? CarIcon : itemType === 'engines' ? EngineIcon : PartsIcon;

  const handleApply = async () => {
    const key  = itemType === 'vehicles' ? 'vehicles' : itemType === 'engines' ? 'engines' : 'spareParts';
    const list = DB.get(key) || [];
    const result = await DB.setAsync(key, list.map(i =>
      i.id === item.id
        ? {
            ...i,
            purchasePrice:     Number(costs.purchasePrice)     || 0,
            auctionFee:        Number(costs.auctionFee)        || 0,
            domesticTransport: Number(costs.domesticTransport) || 0,
            inspectionFee:     Number(costs.inspectionFee)     || 0,
            exportDocs:        Number(costs.exportDocs)        || 0,
            marginAmount:      Number(costs.marginAmount)      || 0,
            containerShare:    Number(contShare) || 0,
            customCharges:     customCharges,
            priceJPY:          Math.round(fob),
          }
        : i
    ));
    if (result && !result.success) {
      alert('Failed to save pricing. Please try again.');
      console.error('[UnitPricingRow] Save failed:', result.error);
    }
    onSave();
  };

  return (
    <div className={`pricing-card${open ? ' open' : ''}`}>
      <div className="pricing-card-header" onClick={() => setOpen(o => !o)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
          <div className="pricing-card-icon"><ItemIcon /></div>
          <div style={{ minWidth: 0 }}>
            <div className="pricing-card-title" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {name}{item.year ? ` (${item.year})` : ''}
            </div>
            <div className="pricing-card-sub">
              {item.sku}
              {item.chassis && <span style={{ marginLeft: 6, fontWeight: 600 }}>· {item.chassis}</span>}
              {containerName && <span style={{ marginLeft: 6, color: 'var(--blue)' }}>· {containerName}</span>}
            </div>
            {item.score && <div style={{ fontSize: 10, color: 'var(--text3)' }}>Grade: {item.score}{item.mileage ? ` · ${item.mileage?.toLocaleString()} km` : ''}{item.color ? ` · ${item.color}` : ''}</div>}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexShrink: 0 }}>
          {containerShare > 0 && (
            <div style={{ textAlign: 'right' }}>
              <div className="pricing-meta-lbl">Container share</div>
              <div className="pricing-meta-val blue">+{formatPrice(containerShare, currency)}</div>
            </div>
          )}
          <div style={{ textAlign: 'right' }}>
            <div className="pricing-meta-lbl">FOB Price</div>
            <div className={`pricing-meta-val${item.priceJPY ? ' green' : ' red'}`}>
              {item.priceJPY ? formatPrice(item.priceJPY, currency) : 'Not set'}
            </div>
          </div>
          <span className="pricing-chevron">{open ? '▲' : '▼'}</span>
        </div>
      </div>

      {/* Unit details expandable */}
      {open && (
        <div style={{ padding: "0 16px", borderBottom: "1px solid var(--border)" }}>
          <button onClick={() => setShowDetails(!showDetails)} style={{ background: "none", border: "none", color: "var(--blue)", cursor: "pointer", fontSize: 11, padding: "8px 0", width: "100%", textAlign: "left" }}>
            {showDetails ? "▲ Hide Unit Details" : "▼ Show Unit Details"}
          </button>
          {showDetails && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 6, fontSize: 11, paddingBottom: 10 }}>
              {[["Make", item.make], ["Model", item.model], ["Year", item.year],
                ["Chassis", item.chassis], ["Engine", item.engineCode], ["Displacement", item.displacement],
                ["Color", item.color], ["Transmission", item.transmission], ["Drive", item.driveSystem],
                ["Steering", item.steer], ["Grade", item.score || item.grade], ["Mileage", item.mileage ? item.mileage.toLocaleString() + " km" : ""],
                ["Category", item.category], ["Condition", item.condition],
              ].filter(function(r) { return r[1]; }).map(function(r) { return (
                <div key={r[0]}><span style={{ color: "var(--text3)" }}>{r[0]}: </span><strong>{r[1]}</strong></div>
              ); })}
            </div>
          )}
        </div>
      )}
        <div className="pricing-card-body">
          <div className="pricing-two-col">

            {/* ── Left: unit cost fields ── */}
            <div>
              <div className="pricing-section-title">Unit Cost Breakdown (JPY)</div>
              <div className="pricing-field-list">

                {/* Purchase Price */}
                <div className="pricing-field-row">
                  <div className="pricing-field-meta">
                    <div className="pricing-field-label">Purchase / Auction Price</div>
                    <div className="pricing-field-hint">Price paid at auction or to dealer</div>
                  </div>
                  <div className="pricing-input-wrap">
                    <span className="pricing-currency">¥</span>
                    <input
                      type="number" className="pricing-input" min="0"
                      value={costs.purchasePrice}
                      onChange={e => handlePurchasePriceChange(e.target.value)}
                      placeholder="450000"
                    />
                  </div>
                </div>

                {/* Auction Fee — fixed or % of purchase price */}
                <div className="pricing-field-row">
                  <div className="pricing-field-meta">
                    <div className="pricing-field-label">Auction / Agent Commission</div>
                    <div className="pricing-field-hint">
                      {auctionMode === 'pct'
                        ? `${auctionPct}% of purchase = ¥${Math.round(Number(costs.purchasePrice) * auctionPct / 100).toLocaleString()}`
                        : 'Fixed fee charged by auction house / agent'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <ModeToggle mode={auctionMode} onChange={handleAuctionModeChange} />
                    {auctionMode === 'fixed' ? (
                      <div className="pricing-input-wrap">
                        <span className="pricing-currency">¥</span>
                        <input
                          type="number" className="pricing-input" min="0"
                          value={costs.auctionFee}
                          onChange={e => set('auctionFee', e.target.value)}
                          placeholder="22500"
                        />
                      </div>
                    ) : (
                      <div className="pricing-input-wrap">
                        <input
                          type="number" className="pricing-input" min="0" max="100" step="0.5"
                          value={auctionPct}
                          onChange={e => handleAuctionPctChange(e.target.value)}
                          placeholder="5"
                          style={{ width: 60 }}
                        />
                        <span className="pricing-currency">%</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Domestic Transport */}
                <FixedField
                  fieldKey="domesticTransport"
                  label="Domestic Transport"
                  hint="Inland haulage from auction/dealer to port yard (¥20k–80k)"
                  placeholder="35000"
                  costs={costs}
                  setCosts={setCosts}
                />

                {/* Inspection */}
                <FixedField
                  fieldKey="inspectionFee"
                  label="Inspection & De-pollution"
                  hint="Export inspection + fluid drain / removal (¥10k–20k)"
                  placeholder="12000"
                  costs={costs}
                  setCosts={setCosts}
                />

                {/* Export Docs */}
                <FixedField
                  fieldKey="exportDocs"
                  label="Export Documentation"
                  hint="Customs clearance, export certificate, de-reg (¥10k–25k)"
                  placeholder="15000"
                  costs={costs}
                  setCosts={setCosts}
                />

              </div>
            </div>

            {/* ── Right: FOB calculation ── */}
            <div>
              <div className="pricing-section-title">FOB Calculation</div>
              <div className="pricing-summary-box">

                <div className="pricing-sum-row">
                  <span>Purchase Price</span>
                  <span>{formatPrice(Number(costs.purchasePrice) || 0, currency)}</span>
                </div>
                <div className="pricing-sum-row">
                  <span>
                    Commission
                    {auctionMode === 'pct' && (
                      <span className="pricing-pct-badge">{auctionPct}%</span>
                    )}
                  </span>
                  <span>{formatPrice(Number(costs.auctionFee) || 0, currency)}</span>
                </div>
                <div className="pricing-sum-row">
                  <span>Domestic Transport</span>
                  <span>{formatPrice(Number(costs.domesticTransport) || 0, currency)}</span>
                </div>
                <div className="pricing-sum-row">
                  <span>Inspection & De-poll</span>
                  <span>{formatPrice(Number(costs.inspectionFee) || 0, currency)}</span>
                </div>
                <div className="pricing-sum-row">
                  <span>Export Docs</span>
                  <span>{formatPrice(Number(costs.exportDocs) || 0, currency)}</span>
                </div>

                <div className="pricing-sum-divider" />
                <div className="pricing-sum-row bold">
                  <span>Unit Subtotal</span>
                  <span>{formatPrice(unitSubtotal, currency)}</span>
                </div>
                <div className="pricing-sum-row blue" style={{ alignItems: 'center' }}>
                  <span>+ Container Share</span>
                  <div className="pricing-input-wrap" style={{ width: 110 }}>
                    <span className="pricing-currency">¥</span>
                    <input type="number" className="pricing-input" min="0"
                      value={contShare} onChange={e => setContShare(e.target.value)} placeholder="0" />
                  </div>
                </div>
                {/* Dynamic custom charges */}
                {customCharges.map((cc, i) => (
                  <div key={i} className="pricing-sum-row" style={{ alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span>+</span>
                      <input className="pricing-input" value={cc.label} onChange={e => setCustomCharges(prev => prev.map((c, j) => j === i ? { ...c, label: e.target.value } : c))}
                        placeholder="Label" style={{ width: 80, fontSize: 10, textAlign: 'left' }} />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <div className="pricing-input-wrap" style={{ width: 90 }}>
                        <span className="pricing-currency">¥</span>
                        <input type="number" className="pricing-input" min="0"
                          value={cc.amount} onChange={e => setCustomCharges(prev => prev.map((c, j) => j === i ? { ...c, amount: e.target.value } : c))} placeholder="0" />
                      </div>
                      <button onClick={() => setCustomCharges(prev => prev.filter((_, j) => j !== i))}
                        style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: 12, padding: '0 2px' }}>×</button>
                    </div>
                  </div>
                ))}
                <button onClick={() => setCustomCharges(prev => [...prev, { label: '', amount: 0 }])}
                  style={{ background: 'none', border: 'none', color: 'var(--blue)', cursor: 'pointer', fontSize: 10, padding: '4px 0' }}>
                  + Add Custom Charge
                </button>
                <div className="pricing-sum-divider" />

                {/* Margin — fixed or % of cost base */}
                <div className="pricing-sum-row" style={{ alignItems: 'flex-start', gap: 8 }}>
                  <div>
                    <span>Profit Margin</span>
                    {marginMode === 'pct' && (
                      <div className="pricing-field-hint" style={{ marginTop: 2 }}>
                        {marginPct}% of cost = ¥{Math.round(getCostBase(costs) * marginPct / 100).toLocaleString()}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <ModeToggle mode={marginMode} onChange={handleMarginModeChange} />
                    {marginMode === 'fixed' ? (
                      <div className="pricing-input-wrap" style={{ width: 130 }}>
                        <span className="pricing-currency">¥</span>
                        <input
                          type="number" className="pricing-input" min="0"
                          value={costs.marginAmount}
                          onChange={e => set('marginAmount', e.target.value)}
                          placeholder="0"
                        />
                      </div>
                    ) : (
                      <div className="pricing-input-wrap" style={{ width: 90 }}>
                        <input
                          type="number" className="pricing-input" min="0" max="200" step="0.5"
                          value={marginPct}
                          onChange={e => handleMarginPctChange(e.target.value)}
                          placeholder="10"
                          style={{ width: 50 }}
                        />
                        <span className="pricing-currency">%</span>
                      </div>
                    )}
                  </div>
                </div>

                {Number(costs.marginAmount) > 0 && (
                  <div className="pricing-sum-hint" style={{ textAlign: 'right' }}>
                    {marginOnCost}% markup on cost · {marginOnFOB}% of FOB
                  </div>
                )}

                <div className="pricing-sum-divider" />
                <div className="pricing-fob-row">
                  <span>FOB Price</span>
                  <span className="pricing-fob-val">{formatPrice(Math.round(fob), currency)}</span>
                </div>

                {item.priceJPY > 0 && priceChanged && (
                  <div className="pricing-sum-hint" style={{ color: 'var(--yellow)', marginTop: 6 }}>
                    ⚠ Current: {formatPrice(item.priceJPY, currency)} → {formatPrice(Math.round(fob), currency)}
                  </div>
                )}
              </div>

              <button
                className="btn btn-success"
                style={{ width: '100%', marginTop: 12 }}
                onClick={handleApply}
                disabled={fob <= 0}
              >
                <CheckIcon />
                {item.priceJPY ? 'Update FOB Price' : 'Apply FOB Price'}
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}

// ─── Bulk Pricing Group ───────────────────────────────────────
function BulkPricingGroup({ label, items, itemType, containerMap, currency, onSave }) {
  const [open, setOpen]     = useState(false);
  const [shared, setShared] = useState({
    auctionPct:        5,
    domesticTransport: 35000,
    inspectionFee:     12000,
    exportDocs:        15000,
    marginPct:         10,
  });
  const setS = (k, v) => setShared(prev => ({ ...prev, [k]: v }));

  const calcFOB = (item) => {
    const pp = Number(item.purchasePrice) || Number(item.costJPY) || 0;
    if (!pp) return 0;
    const auction   = Math.round(pp * Number(shared.auctionPct) / 100);
    const subtotal  = pp + auction + Number(shared.domesticTransport) + Number(shared.inspectionFee) + Number(shared.exportDocs);
    const contShare = containerMap[item.id]?.share || 0;
    const margin    = Math.round((subtotal + contShare) * Number(shared.marginPct) / 100);
    return Math.round(subtotal + contShare + margin);
  };

  const priced           = items.filter(i => i.priceJPY > 0).length;
  const unpricedItems    = items.filter(i => !i.priceJPY);
  const hasPurchasePrice = items.some(i => (Number(i.purchasePrice) || Number(i.costJPY)) > 0);

  const applyToItems = async (targetItems) => {
    const key = itemType === 'vehicles' ? 'vehicles' : itemType === 'engines' ? 'engines' : 'spareParts';
    const all = DB.get(key) || [];
    const ids = new Set(targetItems.map(i => i.id));
    const result = await DB.setAsync(key, all.map(item => {
      if (!ids.has(item.id)) return item;
      const pp = Number(item.purchasePrice) || Number(item.costJPY) || 0;
      if (!pp) return item;
      const auction   = Math.round(pp * Number(shared.auctionPct) / 100);
      const subtotal  = pp + auction + Number(shared.domesticTransport) + Number(shared.inspectionFee) + Number(shared.exportDocs);
      const contShare = containerMap[item.id]?.share || 0;
      const margin    = Math.round((subtotal + contShare) * Number(shared.marginPct) / 100);
      return {
        ...item,
        purchasePrice:     pp,
        auctionFee:        auction,
        domesticTransport: Number(shared.domesticTransport),
        inspectionFee:     Number(shared.inspectionFee),
        exportDocs:        Number(shared.exportDocs),
        marginAmount:      margin,
        containerShare:    contShare,
        priceJPY:          Math.round(subtotal + contShare + margin),
      };
    }));
    if (result && !result.success) {
      alert('Failed to save bulk pricing. Please try again.');
      console.error('[BulkPricingApply] Save failed:', result.error);
    }
    onSave();
  };

  const ItemIcon = itemType === 'vehicles' ? CarIcon : itemType === 'engines' ? EngineIcon : PartsIcon;
  const pct      = items.length > 0 ? Math.round((priced / items.length) * 100) : 0;

  return (
    <div className={`pricing-card${open ? ' open' : ''}`}>
      <div className="pricing-card-header" onClick={() => setOpen(o => !o)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div className="pricing-card-icon"><ItemIcon /></div>
          <div>
            <div className="pricing-card-title">{label}</div>
            <div className="pricing-card-sub">{items.length} unit{items.length !== 1 ? 's' : ''}</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div className="pricing-bulk-progress">
            <div className="pricing-bulk-bar" style={{ width: `${pct}%` }} />
          </div>
          <span style={{ fontSize: 12, color: 'var(--text3)', minWidth: 65 }}>
            {priced}/{items.length} priced
          </span>
          <span className="pricing-chevron">{open ? '▲' : '▼'}</span>
        </div>
      </div>

      {open && (
        <div className="pricing-card-body">
          <div className="pricing-two-col">

            {/* Left: shared cost fields */}
            <div>
              <div className="pricing-section-title">Shared Costs — Applied to All {items.length} Units</div>
              <div className="pricing-field-hint" style={{ marginBottom: 12 }}>
                Purchase price is read from each unit individually. Set overhead once below.
              </div>
              <div className="pricing-field-list">

                <div className="pricing-field-row">
                  <div className="pricing-field-meta">
                    <div className="pricing-field-label">Auction / Agent Commission</div>
                    <div className="pricing-field-hint">% of each unit's purchase price</div>
                  </div>
                  <div className="pricing-input-wrap" style={{ width: 110 }}>
                    <input type="number" className="pricing-input" min="0" max="30" step="0.5"
                      value={shared.auctionPct} onChange={e => setS('auctionPct', e.target.value)}
                      style={{ width: 60 }} />
                    <span className="pricing-currency">%</span>
                  </div>
                </div>

                <div className="pricing-field-row">
                  <div className="pricing-field-meta">
                    <div className="pricing-field-label">Domestic Transport</div>
                    <div className="pricing-field-hint">Inland haulage to port (JPY)</div>
                  </div>
                  <div className="pricing-input-wrap">
                    <span className="pricing-currency">¥</span>
                    <input type="number" className="pricing-input" min="0"
                      value={shared.domesticTransport} onChange={e => setS('domesticTransport', e.target.value)} />
                  </div>
                </div>

                <div className="pricing-field-row">
                  <div className="pricing-field-meta">
                    <div className="pricing-field-label">Inspection & De-pollution</div>
                    <div className="pricing-field-hint">Export inspection + fluid removal (JPY)</div>
                  </div>
                  <div className="pricing-input-wrap">
                    <span className="pricing-currency">¥</span>
                    <input type="number" className="pricing-input" min="0"
                      value={shared.inspectionFee} onChange={e => setS('inspectionFee', e.target.value)} />
                  </div>
                </div>

                <div className="pricing-field-row">
                  <div className="pricing-field-meta">
                    <div className="pricing-field-label">Export Documentation</div>
                    <div className="pricing-field-hint">Customs clearance & certificates (JPY)</div>
                  </div>
                  <div className="pricing-input-wrap">
                    <span className="pricing-currency">¥</span>
                    <input type="number" className="pricing-input" min="0"
                      value={shared.exportDocs} onChange={e => setS('exportDocs', e.target.value)} />
                  </div>
                </div>

                <div className="pricing-field-row">
                  <div className="pricing-field-meta">
                    <div className="pricing-field-label">Profit Margin</div>
                    <div className="pricing-field-hint">% of total cost (unit costs + container share)</div>
                  </div>
                  <div className="pricing-input-wrap" style={{ width: 110 }}>
                    <input type="number" className="pricing-input" min="0" max="200" step="0.5"
                      value={shared.marginPct} onChange={e => setS('marginPct', e.target.value)}
                      style={{ width: 60 }} />
                    <span className="pricing-currency">%</span>
                  </div>
                </div>

              </div>

              {!hasPurchasePrice && (
                <div className="pricing-sum-hint" style={{ color: 'var(--yellow)', marginTop: 10 }}>
                  ⚠ No purchase prices found. Enter individual prices in the "Unit Pricing" tab first,
                  or make sure units were added with a cost price.
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                {unpricedItems.length > 0 && (
                  <button className="btn btn-primary" style={{ flex: 1 }}
                    onClick={() => applyToItems(unpricedItems)} disabled={!hasPurchasePrice}>
                    <CheckIcon /> Price Unpriced ({unpricedItems.length})
                  </button>
                )}
                <button className="btn btn-success" style={{ flex: 1 }}
                  onClick={() => applyToItems(items)} disabled={!hasPurchasePrice}>
                  <CheckIcon /> Apply to All ({items.length})
                </button>
              </div>
            </div>

            {/* Right: unit list with editable purchase prices */}
            <div>
              <div className="pricing-section-title">Units — Enter Purchase Prices</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 400, overflowY: 'auto' }}>
                {items.map(item => {
                  const pp = Number(item.purchasePrice) || Number(item.costJPY) || 0;
                  const fob = calcFOB(item);
                  const name = item.make ? `${item.make} ${item.model}` : item.engineName || item.partName || '';
                  return (
                    <div key={item.id} className="pricing-bulk-unit-row" style={{ padding: '6px 8px' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 11, fontWeight: 600 }}>{name || item.sku}</div>
                        <div style={{ fontSize: 9, color: 'var(--cyan)', fontFamily: 'monospace' }}>{item.sku}{item.chassis ? ` · ${item.chassis}` : ''}</div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                        <span style={{ fontSize: 10, color: 'var(--text3)' }}>¥</span>
                        <input
                          type="text" inputMode="decimal"
                          value={pp || ''}
                          onFocus={e => e.target.select()}
                          onChange={e => {
                            const val = parseFloat(e.target.value.replace(/[^0-9]/g, '')) || 0;
                            const key = item.make ? 'vehicles' : item.engineName ? 'engines' : 'spareParts';
                            const list = DB.get(key) || [];
                            DB.setAsync(key, list.map(u => u.id === item.id ? { ...u, purchasePrice: val, costJPY: val } : u)).then(result => {
                              if (result && !result.success) {
                                alert('Failed to save purchase price. Please try again.');
                                console.error('[InlinePurchasePrice] Save failed:', result.error);
                              }
                            });
                          }}
                          onBlur={() => onSave()}
                          placeholder="Purchase ¥"
                          style={{ width: 80, fontSize: 11, padding: '3px 6px', textAlign: 'right', background: 'var(--bg1)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text)', fontFamily: 'monospace' }}
                        />
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0, minWidth: 60 }}>
                        {fob > 0 && <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--green)' }}>{formatPrice(fob, currency)}</div>}
                        {item.priceJPY > 0 && <div style={{ fontSize: 9, color: 'var(--text3)' }}>cur: {formatPrice(item.priceJPY, currency)}</div>}
                      </div>
                      <span style={{ fontSize: 12, flexShrink: 0, color: item.priceJPY > 0 ? 'var(--green)' : pp > 0 ? 'var(--yellow)' : 'var(--text3)' }}>
                        {item.priceJPY > 0 ? '✓' : pp > 0 ? '◐' : '○'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}

// ─── Bulk Pricing Tab ─────────────────────────────────────────
function BulkPricingTab({ vehicles, engines, parts, containerMap, currency, onSave }) {
  const [typeFilter,  setTypeFilter]  = useState('vehicles');
  const [monthFilter, setMonthFilter] = useState('all');
  const [makeFilter,  setMakeFilter]  = useState('all');

  const getMonthKey   = (d) => (d ? d.slice(0, 7) : '0000-00');
  const getMonthLabel = (key) => {
    if (key === '0000-00') return 'Unknown date';
    const [y, m] = key.split('-');
    return new Date(Number(y), Number(m) - 1, 1)
      .toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const allItems = typeFilter === 'vehicles' ? vehicles : typeFilter === 'engines' ? engines : parts;

  const allMonths = [...new Set(allItems.map(i => getMonthKey(i.createdAt)))]
    .sort().reverse();
  const allMakes  = typeFilter === 'vehicles'
    ? [...new Set(vehicles.map(v => v.make).filter(Boolean))].sort()
    : [];

  const filtered = allItems.filter(item => {
    const monthOk = monthFilter === 'all' || getMonthKey(item.createdAt) === monthFilter;
    const makeOk  = makeFilter  === 'all' || item.make === makeFilter;
    return monthOk && makeOk;
  });

  // Group items by make+model+year (vehicles) / manufacturer (engines) / category (parts)
  const groupMap = {};
  filtered.forEach(item => {
    let key, label;
    if (typeFilter === 'vehicles') {
      const make  = item.make  || 'Unknown';
      const model = item.model || 'Unknown';
      const year  = item.year  || '';
      key   = `${make}|${model}|${year}`;
      label = `${make} ${model}${year ? ` (${year})` : ''}`;
    } else if (typeFilter === 'engines') {
      key   = item.manufacturer || 'Unknown';
      label = `${item.manufacturer || 'Unknown'} Engines`;
    } else {
      key   = item.category || 'Uncategorized';
      label = `${item.category || 'Uncategorized'} Parts`;
    }
    if (!groupMap[key]) groupMap[key] = { key, label, items: [] };
    groupMap[key].items.push(item);
  });
  const groups = Object.values(groupMap).sort((a, b) => b.items.length - a.items.length);

  const totalPriced = filtered.filter(i => i.priceJPY > 0).length;

  return (
    <div>
      {/* Tip banner */}
      <div className="pricing-bulk-tip">
        <strong>How it works:</strong> Set shared overhead costs once per group (auction %, transport, inspection, docs, margin).
        Each unit's purchase price is read individually from inventory — so you only fill in 5 fields
        to price an entire batch. Filter by <strong>month</strong> to handle auction batches (e.g. "all January cars").
      </div>

      {/* Filter bar */}
      <div className="pricing-bulk-filters">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <div className="pricing-mode-toggle">
            {[['vehicles', 'Vehicles'], ['engines', 'Engines'], ['parts', 'Parts']].map(([v, l]) => (
              <button key={v}
                className={`pricing-mode-btn${typeFilter === v ? ' active' : ''}`}
                onClick={() => { setTypeFilter(v); setMakeFilter('all'); setMonthFilter('all'); }}>
                {l}
              </button>
            ))}
          </div>
          <select className="filter-select" value={monthFilter} onChange={e => setMonthFilter(e.target.value)}>
            <option value="all">All months</option>
            {allMonths.map(m => <option key={m} value={m}>{getMonthLabel(m)}</option>)}
          </select>
          {typeFilter === 'vehicles' && (
            <select className="filter-select" value={makeFilter} onChange={e => setMakeFilter(e.target.value)}>
              <option value="all">All makes</option>
              {allMakes.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          )}
        </div>
        <div style={{ fontSize: 13, color: 'var(--text3)', whiteSpace: 'nowrap' }}>
          {totalPriced} / {filtered.length} priced
        </div>
      </div>

      {/* Group cards */}
      {groups.length === 0 ? (
        <div className="empty" style={{ marginTop: 20 }}>
          <PackageIcon />
          <div className="empty-title">No items match this filter</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 14 }}>
          {groups.map(g => (
            <BulkPricingGroup
              key={g.key}
              label={g.label}
              items={g.items}
              itemType={typeFilter}
              containerMap={containerMap}
              currency={currency}
              onSave={onSave}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Pricing Page ────────────────────────────────────────
function Pricing({ currency, setPage }) {
  const [tab,     setTab]     = useState('containers');
  const [unitTab, setUnitTab] = useState('vehicles');
  const [, forceUpdate]       = useState();
  const [unitSearch, setUnitSearch] = useState('');
  const [showFilter, setShowFilter] = useState('all');
  const [editingFees, setEditingFees] = useState(false);
  const [feeValues, setFeeValues] = useState(null);
  const [feeSaved, setFeeSaved] = useState(false);

  const containers = DB.get('containers') || [];
  const vehicles   = DB.get('vehicles')   || [];
  const engines    = DB.get('engines')    || [];
  const parts      = DB.get('spareParts') || [];
  const allUnits   = [...vehicles, ...engines, ...parts];

  // Build containerShare map: unitId → { share, containerName }
  const containerMap = {};
  containers.forEach(cont => {
    const ids       = cont.units || [];
    const count     = ids.length || 1;
    const total = calculateContainerTotalJPY(cont.costs);
    const share = Math.round(total / count);
    ids.forEach(id => { containerMap[id] = { share, name: cont.number }; });
  });

  // Stats
  const totalUnits   = allUnits.length;
  const pricedUnits  = allUnits.filter(u => u.priceJPY > 0).length;
  const unpricedUnits = totalUnits - pricedUnits;
  const totalFOB     = allUnits.reduce((s, u) => s + (u.priceJPY || 0), 0);

  const currentItems =
    unitTab === 'vehicles' ? vehicles :
    unitTab === 'engines'  ? engines  : parts;

  const refresh = () => forceUpdate({});

  return (
    <div>
      {/* Header */}
      <div className="pricing-page-header" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <span className="pricing-page-icon"><CalculatorIcon /></span>
        <h2 style={{ margin: 0 }}>Pricing & FOB Calculator</h2>
      </div>

      {/* FOB Formula banner */}
      <div className="pricing-formula-banner">
        <div className="pricing-formula-banner-title">How FOB is Calculated</div>
        <div className="pricing-formula-steps">
          <div className="pricing-formula-step">
            <div className="pricing-formula-step-num">1</div>
            <div>
              <div className="pricing-formula-step-name">Unit Costs</div>
              <div className="pricing-formula-step-desc">Purchase + Auction Fee + Transport + Inspection + Docs</div>
            </div>
          </div>
          <div className="pricing-formula-plus">+</div>
          <div className="pricing-formula-step">
            <div className="pricing-formula-step-num">2</div>
            <div>
              <div className="pricing-formula-step-name">Container Share</div>
              <div className="pricing-formula-step-desc">Freight + Drayage + Port + B/L + Loading ÷ Units</div>
            </div>
          </div>
          <div className="pricing-formula-plus">+</div>
          <div className="pricing-formula-step">
            <div className="pricing-formula-step-num">3</div>
            <div>
              <div className="pricing-formula-step-name">Profit Margin</div>
              <div className="pricing-formula-step-desc">Your markup per unit</div>
            </div>
          </div>
          <div className="pricing-formula-eq">=</div>
          <div className="pricing-formula-step highlight">
            <div className="pricing-formula-step-num">FOB</div>
            <div>
              <div className="pricing-formula-step-name">Free On Board Price</div>
              <div className="pricing-formula-step-desc">Delivered to vessel at Japan port</div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid" style={{ marginBottom: 20 }}>
        <StatCard icon={PackageIcon}     value={`${pricedUnits} / ${totalUnits}`}  label="Units Priced"     color="green"  />
        <StatCard icon={DollarSignIcon}  value={formatPrice(totalFOB, currency)}   label="Total FOB Value"  color="blue"   />
        <StatCard icon={ContainerIcon}   value={containers.length}                  label="Containers"       color="cyan"   />
        <StatCard icon={CalculatorIcon}  value={unpricedUnits}                      label="Needs Pricing"    color={unpricedUnits > 0 ? 'orange' : 'green'} />
      </div>

      {/* Tabs */}
      <div className="tabs" style={{ marginBottom: 16 }}>
        <button className={`tab ${tab === 'containers' ? 'active' : ''}`} onClick={() => setTab('containers')}>
          Container Costs
          <span className="cnt">{containers.length}</span>
        </button>
        <button className={`tab ${tab === 'units' ? 'active' : ''}`} onClick={() => setTab('units')}>
          Unit Pricing
          <span className="cnt">{totalUnits}</span>
          {unpricedUnits > 0 && <span className="cnt" style={{ background: 'var(--orange)', marginLeft: 4 }}>{unpricedUnits} unpriced</span>}
        </button>
        <button className={`tab ${tab === 'spreadsheet' ? 'active' : ''}`} onClick={() => setTab('spreadsheet')}>
          Spreadsheet
          <span className="cnt">{allUnits.length}</span>
        </button>
        <button className={`tab ${tab === 'fees' ? 'active' : ''}`} onClick={() => setTab('fees')}>
          Default Fees
        </button>
      </div>

      {/* ── Container Costs tab ── */}
      {tab === 'containers' && (
        <div>
          {containers.length === 0 ? (
            <div className="empty">
              <ContainerIcon />
              <div className="empty-title">No containers yet</div>
              <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 6 }}>
                Add containers first, then set their freight costs here.
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {containers.map(cont => (
                <ContainerCostCard
                  key={cont.id}
                  container={cont}
                  currency={currency}
                  setPage={setPage}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Bulk Pricing tab ── */}
      {tab === 'bulk' && (
        <BulkPricingTab
          vehicles={vehicles}
          engines={engines}
          parts={parts}
          containerMap={containerMap}
          currency={currency}
          onSave={refresh}
        />
      )}

      {/* ── Unit Pricing tab ── */}
      {tab === 'units' && (() => {
        
        

        let filtered = currentItems;
        if (showFilter === 'unpriced') filtered = filtered.filter(i => !i.priceJPY);
        if (showFilter === 'priced') filtered = filtered.filter(i => i.priceJPY > 0);
        if (unitSearch) {
          const q = unitSearch.toLowerCase();
          filtered = filtered.filter(i => {
            const fields = [i.make, i.model, i.sku, i.chassis, i.engineName, i.partName, i.year?.toString()].filter(Boolean).join(' ').toLowerCase();
            return fields.includes(q);
          });
        }

        return (
          <div>
            {/* Sub-tabs + search */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
              <div className="tabs" style={{ marginBottom: 0 }}>
                <button className={`tab ${unitTab === 'vehicles' ? 'active' : ''}`} onClick={() => setUnitTab('vehicles')}>
                  Vehicles <span className="cnt">{vehicles.length}</span>
                </button>
                <button className={`tab ${unitTab === 'engines' ? 'active' : ''}`} onClick={() => setUnitTab('engines')}>
                  Engines <span className="cnt">{engines.length}</span>
                </button>
                <button className={`tab ${unitTab === 'parts' ? 'active' : ''}`} onClick={() => setUnitTab('parts')}>
                  Parts <span className="cnt">{parts.length}</span>
                </button>
              </div>
              <div style={{ flex: 1 }} />
              <select className="inv-filter-select" value={showFilter} onChange={e => setShowFilter(e.target.value)}>
                <option value="all">All</option>
                <option value="unpriced">Needs Pricing</option>
                <option value="priced">Priced</option>
              </select>
              <div className="inv-search-wrap">
                <input className="inv-search" placeholder="Search SKU, chassis, make..." value={unitSearch} onChange={e => setUnitSearch(e.target.value)} style={{ width: 200 }} />
              </div>
              <span style={{ fontSize: 11, color: 'var(--text3)' }}>{filtered.length} units</span>
            </div>

            {filtered.length === 0 ? (
              <div className="empty">
                <PackageIcon />
                <div className="empty-title">{unitSearch ? 'No units match' : 'No items in this category'}</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {filtered.map(item => (
                  <UnitPricingRow
                    key={item.id}
                    item={item}
                    itemType={unitTab}
                    containerShare={containerMap[item.id]?.share || 0}
                    containerName={containerMap[item.id]?.name || null}
                    currency={currency}
                    onSave={refresh}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Spreadsheet tab (grouped by container) ── */}
      {tab === 'spreadsheet' && (
        <SpreadsheetPricing allUnits={allUnits} containerMap={containerMap} currency={currency} onSave={refresh} containers={containers} />
      )}

      {/* ── Default Fees tab ── */}
      {tab === 'fees' && (() => {
        const stored = (() => {
          try { const all = DB.get('appConfig'); if (all && typeof all === 'object' && !Array.isArray(all)) return all.fee_defaults || {}; } catch {} return {};
        })();
        const fees = { flat_charge_pct: 8, recycle_fee_pct: 3, vat_pct: 10, nikko_pct: 1, auction_fee_fixed_jpy: 17000, transport_default_jpy: 6000, vanning_default_jpy: 4075, ...stored };
        const editing = editingFees; const setEditing = setEditingFees;
        const f = feeValues || fees; const setF = setFeeValues;
        const saved = feeSaved; const setSaved = setFeeSaved;

        const FIELDS = [
          { key: 'flat_charge_pct', label: 'Flat Charge', type: '%' },
          { key: 'recycle_fee_pct', label: 'Recycle Fee', type: '%' },
          { key: 'vat_pct', label: 'VAT', type: '%' },
          { key: 'nikko_pct', label: 'Company Commission', type: '%' },
          { key: 'auction_fee_fixed_jpy', label: 'Auction Fee', type: '¥' },
          { key: 'transport_default_jpy', label: 'Transport Default', type: '¥' },
          { key: 'vanning_default_jpy', label: 'Vanning Default', type: '¥' },
        ];

        const saveFees = async () => {
          const cfg = typeof DB.get('appConfig') === 'object' && !Array.isArray(DB.get('appConfig')) ? DB.get('appConfig') : {};
          const result = await DB.setAsync('appConfig', { ...cfg, fee_defaults: f });
          if (result && !result.success) {
            alert('Fee defaults could not be saved — please try again.');
            console.error('[saveFees] Save failed:', result.error);
            return;
          }
          setEditing(false); setSaved(true); setTimeout(() => setSaved(false), 2500);
        };

        return (
          <div className="card">
            <div className="card-header">
              <div className="card-title">Default Fees</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {saved && <span style={{ fontSize: 11, color: 'var(--green)', fontWeight: 600 }}>Saved!</span>}
                {!editing && <button className="btn btn-secondary btn-sm" onClick={() => setEditing(true)}>Edit</button>}
              </div>
            </div>
            <div className="card-body">
              <p style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 14 }}>
                These defaults auto-fill when pricing a unit. You can override per unit.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
                {FIELDS.map(ff => (
                  <div key={ff.key} style={{ padding: '10px 14px', background: 'var(--bg3)', borderRadius: 8 }}>
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 4 }}>{ff.label}</div>
                    {editing ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <input type="number" className="form-input" value={f[ff.key] ?? ''} onChange={e => setF(prev => ({ ...prev, [ff.key]: e.target.value }))}
                          style={{ fontSize: 14, padding: '4px 6px', flex: 1 }} />
                        <span style={{ fontSize: 12, color: 'var(--text3)' }}>{ff.type}</span>
                      </div>
                    ) : (
                      <div style={{ fontSize: 16, fontWeight: 700 }}>
                        {ff.type === '¥' ? `¥${Number(fees[ff.key] || 0).toLocaleString()}` : `${fees[ff.key] || 0}%`}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {editing && (
                <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => { setEditing(false); setF(fees); }}>Cancel</button>
                  <button className="btn btn-primary btn-sm" onClick={saveFees}><CheckIcon /> Save Defaults</button>
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ── Spreadsheet Bulk Pricing ─────────────────────────────────
const fmtNum = (v) => {
  if (v === null || v === undefined || v === '' || v === 0) return '';
  const n = typeof v === 'number' ? v : parseFloat(v);
  if (isNaN(n)) return v;
  return n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const SpreadsheetCell = React.memo(function SpreadsheetCell({ value, onChange, style, baseCurrency, jpyAed, jpyUsd }) {
  const [focused, setFocused] = React.useState(false);
  const [showCur, setShowCur] = React.useState(false);
  const [inputCur, setInputCur] = React.useState(null); // null = base currency
  const cellRef = React.useRef(null);
  const displayVal = focused ? (value === 0 ? '' : value) : fmtNum(value);

  // Close currency picker on outside click
  React.useEffect(() => {
    if (!showCur) return;
    const handler = (e) => { if (cellRef.current && !cellRef.current.contains(e.target)) setShowCur(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showCur]);

  const handleChange = (rawVal) => {
    const num = parseFloat(rawVal.replace(/,/g, '')) || 0;
    if (!inputCur || !baseCurrency || inputCur === baseCurrency) {
      onChange(num);
    } else {
      // Convert to base currency
      const rate = jpyAed || getJpyRate('AED');
      const rateUsd = jpyUsd || getJpyRate('USD');
      let converted = num;
      if (baseCurrency === 'JPY') {
        if (inputCur === 'USD') converted = rateUsd > 0 ? Math.round(num / rateUsd) : num;
        else if (inputCur === 'AED') converted = rate > 0 ? Math.round(num / rate) : num;
      } else if (baseCurrency === 'AED') {
        if (inputCur === 'USD') converted = rateUsd > 0 && rate > 0 ? Math.round(num / rateUsd * rate) : num;
        else if (inputCur === 'JPY') converted = Math.round(num * rate);
      }
      onChange(converted);
      setInputCur(null); // reset back to base after conversion
    }
  };

  const curOptions = baseCurrency === 'AED'
    ? [{ code: 'AED', symbol: 'د.إ' }, { code: 'USD', symbol: '$' }, { code: 'JPY', symbol: '¥' }]
    : [{ code: 'JPY', symbol: '¥' }, { code: 'USD', symbol: '$' }, { code: 'AED', symbol: 'د.إ' }];

  const activeCur = inputCur || baseCurrency || 'JPY';
  const activeSymbol = curOptions.find(c => c.code === activeCur)?.symbol || '¥';

  return (
    <div ref={cellRef} style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
      <input
        type="text"
        inputMode="decimal"
        value={displayVal}
        onFocus={e => { setFocused(true); e.target.select(); }}
        onBlur={e => { setFocused(false); handleChange(e.target.value); }}
        onChange={e => onChange(e.target.value.replace(/[^0-9.-]/g, ''))}
        style={{ width: '100%', minWidth: 70, padding: '4px 6px', paddingRight: 24, fontSize: 12, fontFamily: 'monospace', textAlign: 'right', background: 'var(--bg1)', border: '1px solid var(--border)', borderRadius: 3, color: 'var(--text)', outline: 'none', ...style }}
        onKeyDown={e => {
          if (e.key === 'Enter' || (e.key === 'Tab' && !e.shiftKey)) {
            e.preventDefault();
            const inputs = e.target.closest('table')?.querySelectorAll('input[type="text"]');
            if (inputs) {
              const idx = Array.from(inputs).indexOf(e.target);
              if (idx >= 0 && inputs[idx + 1]) inputs[idx + 1].focus();
            }
          }
        }}
      />
      <button
        onClick={() => setShowCur(s => !s)}
        style={{
          position: 'absolute', right: 2, top: '50%', transform: 'translateY(-50%)',
          background: 'none', border: 'none', cursor: 'pointer', padding: '0 3px',
          fontSize: 9, color: inputCur && inputCur !== baseCurrency ? 'var(--cyan)' : 'var(--text3)',
          fontWeight: inputCur && inputCur !== baseCurrency ? 700 : 400,
          lineHeight: 1,
        }}
        title="Change input currency"
        tabIndex={-1}
      >
        {activeSymbol}
      </button>
      {showCur && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, zIndex: 20,
          background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6,
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)', overflow: 'hidden', minWidth: 90,
        }}>
          {curOptions.map(c => (
            <button
              key={c.code}
              onClick={() => { setInputCur(c.code === baseCurrency ? null : c.code); setShowCur(false); }}
              style={{
                display: 'block', width: '100%', padding: '6px 10px', border: 'none',
                background: (inputCur || baseCurrency) === c.code ? 'var(--blue)' : 'transparent',
                color: (inputCur || baseCurrency) === c.code ? '#fff' : 'var(--text1)',
                cursor: 'pointer', fontSize: 11, textAlign: 'left', fontWeight: 500,
              }}
            >
              {c.symbol} {c.code}
            </button>
          ))}
        </div>
      )}
    </div>
  );
});

function SpreadsheetPricing({ allUnits, containerMap, currency, onSave, containers }) {
  const [filter, setFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [groupBy, setGroupBy] = useState('container');
  const [searchQ, setSearchQ] = useState('');
  const [changes, setChanges] = useState({});
  const [page, setPage] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const flashTimerRef = React.useRef(null);
  const perPage = 50;

  // Fullscreen toggle
  const toggleFullscreen = () => {
    if (!isFullscreen) {
      const elem = document.documentElement;
      if (elem.requestFullscreen) elem.requestFullscreen();
      else if (elem.webkitRequestFullscreen) elem.webkitRequestFullscreen();
      document.body.classList.add('spreadsheet-fullscreen');
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) document.exitFullscreen();
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      document.body.classList.remove('spreadsheet-fullscreen');
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handler = () => {
      if (!document.fullscreenElement) {
        document.body.classList.remove('spreadsheet-fullscreen');
        setIsFullscreen(false);
      }
    };
    document.addEventListener('fullscreenchange', handler);
    return () => { document.removeEventListener('fullscreenchange', handler); document.body.classList.remove('spreadsheet-fullscreen'); };
  }, []);
  const customers = DB.get('customers') || [];
  const allContainers = containers || DB.get('containers') || [];

  // Exchange rates — editable, with live fetch option
  const savedRates = (() => { try { return DB.get('appConfig')?.exchange_rates_config || {}; } catch { return {}; } })();
  const [jpyAed, setJpyAed] = useState(savedRates.jpy_to_aed || 0.0245);
  const [jpyUsd, setJpyUsd] = useState(savedRates.jpy_to_usd || 0.0067);
  const [rateSource, setRateSource] = useState(savedRates.source || 'manual');
  const [rateUpdated, setRateUpdated] = useState(savedRates.last_updated || '');
  const [fetchingRates, setFetchingRates] = useState(false);

  const saveRates = async () => {
    const cfg = DB.get('appConfig') || {};
    const base = typeof cfg === 'object' && !Array.isArray(cfg) ? cfg : {};
    const result = await DB.setAsync('appConfig', { ...base, exchange_rates_config: { jpy_to_aed: parseFloat(jpyAed), jpy_to_usd: parseFloat(jpyUsd), usd_to_aed: parseFloat(jpyAed) / parseFloat(jpyUsd), source: rateSource, last_updated: new Date().toISOString() } });
    if (result && !result.success) {
      alert('Exchange rates could not be saved — please try again.');
      console.error('[saveRates] Save failed:', result.error);
      return;
    }
    setRateUpdated(new Date().toISOString());
    alert('Rates saved!');
  };

  const fetchLiveRates = async () => {
    setFetchingRates(true);
    try {
      const res = await fetch('https://api.exchangerate-api.com/v4/latest/JPY');
      const data = await res.json();
      if (data?.rates) {
        setJpyUsd(parseFloat(data.rates.USD.toFixed(6)));
        setJpyAed(parseFloat(data.rates.AED.toFixed(6)));
        setRateSource('exchangerate-api.com');
        alert(`Rates fetched!\nJPY->USD: ${data.rates.USD.toFixed(6)}\nJPY->AED: ${data.rates.AED.toFixed(6)}\n\nClick "Save Rates" to apply.`);
      }
    } catch (err) {
      alert('Failed to fetch rates. Enter manually or try again later.');
    }
    setFetchingRates(false);
  };

  const JPY_USD = parseFloat(jpyUsd) || getJpyRate('USD');
  const JPY_AED = parseFloat(jpyAed) || getJpyRate('AED');

  let units = allUnits.map(u => {
    const name = u.make ? `${u.make} ${u.model}` : u.engineName || u.partName || 'Unit';
    const type = u.make ? 'VEH' : u.engineName ? 'ENG' : 'PRT';
    const custName = u.assignedTo ? (customers.find(c => c.id === u.assignedTo)?.name || '') : '';
    return { ...u, _name: name, _type: type, _cust: custName };
  });

  if (filter === 'unpriced') units = units.filter(u => !u.priceJPY && !u.pricing?.fob_price_usd);
  if (typeFilter !== 'all') units = units.filter(u => u._type === typeFilter);
  if (searchQ) {
    const q = searchQ.toLowerCase();
    units = units.filter(u => [u.sku, u.chassis, u.make, u.model, u._name, u.engineName, u.partName].filter(Boolean).join(' ').toLowerCase().includes(q));
  }

  const totalPages = Math.max(1, Math.ceil(units.length / perPage));
  const safePage = Math.min(page, totalPages);
  const pagedUnits = units.slice((safePage - 1) * perPage, safePage * perPage);

  const getVal = (unitId, field, fallback) => changes[unitId]?.[field] ?? fallback ?? 0;
  const setVal = (unitId, field, value) => {
    if (savedFlash) {
      setSavedFlash(false);
      if (flashTimerRef.current) { clearTimeout(flashTimerRef.current); flashTimerRef.current = null; }
    }
    setChanges(prev => ({ ...prev, [unitId]: { ...(prev[unitId] || {}), [field]: value } }));
  };

  // Japan total = auction + recycle + misc + bid + transport + vanning + freight + nikko commission
  const calcJapanTotal = (u) => {
    const g = (f, fb) => Number(getVal(u.id, f, fb)) || 0;
    return g('auction', u.costJPY || u.purchasePrice) + g('recycle', u.pricing?.japan_costs?.recycle_fee) +
      g('misc', u.pricing?.japan_costs?.misc) + g('bid', u.pricing?.japan_costs?.bid_fee) +
      g('transport', u.pricing?.japan_costs?.transport) + g('vanning', u.pricing?.japan_costs?.vanning) +
      g('freight', u.pricing?.japan_costs?.freight) + g('nikko', u.pricing?.japan_costs?.nikko);
  };

  // LOCAL EXP (AED) = FREIGHT (AED) + CLEARING EXP (AED) + YARD (AED)
  const calcLocalExp = (u) => {
    const g = (f, fb) => Number(getVal(u.id, f, fb)) || 0;
    return g('freightAed', u.pricing?.destination_costs?.freight_aed) +
      g('clearing', u.pricing?.destination_costs?.clearing) +
      g('yard', u.pricing?.destination_costs?.yard);
  };

  // UNIT PRICE (AED) = TOTAL (AED) PORT + LOCAL EXP (AED)
  const calcUnitPriceAed = (u) => {
    const japanTotal = calcJapanTotal(u);
    const totalAedPort = japanTotal * JPY_AED;
    return totalAedPort + calcLocalExp(u);
  };

  const saveAll = async () => {
    const keys = Object.keys(changes);
    if (keys.length === 0) return;
    console.log('[saveAll] starting save for', keys.length, 'units');
    // Group changes by DB key to minimize writes
    const byKey = {};
    keys.forEach(unitId => {
      const unit = allUnits.find(u => u.id === unitId);
      if (!unit) return;
      const key = unit.make ? 'vehicles' : unit.engineName ? 'engines' : 'spareParts';
      if (!byKey[key]) byKey[key] = [];
      byKey[key].push(unitId);
    });
    const failed = [];
    const succeeded = [];
    for (const [key, unitIds] of Object.entries(byKey)) {
      const list = DB.get(key) || [];
      const n = (v) => Number(v) || 0;
      const updated = list.map(u => {
        const c = changes[u.id];
        if (!c) return u;
        return {
          ...u,
          costJPY: n(c.auction ?? u.costJPY), purchasePrice: n(c.auction ?? u.purchasePrice),
          pricing: {
            ...(u.pricing || {}),
            japan_costs: {
              ...(u.pricing?.japan_costs || {}),
              auction_price: n(c.auction ?? u.pricing?.japan_costs?.auction_price),
              recycle_fee: n(c.recycle ?? u.pricing?.japan_costs?.recycle_fee),
              misc: n(c.misc ?? u.pricing?.japan_costs?.misc),
              bid_fee: n(c.bid ?? u.pricing?.japan_costs?.bid_fee),
              transport: n(c.transport ?? u.pricing?.japan_costs?.transport),
              vanning: n(c.vanning ?? u.pricing?.japan_costs?.vanning),
              freight: n(c.freight ?? u.pricing?.japan_costs?.freight),
              nikko: n(c.nikko ?? u.pricing?.japan_costs?.nikko),
            },
            destination_costs: {
              ...(u.pricing?.destination_costs || {}),
              freight_aed: n(c.freightAed ?? u.pricing?.destination_costs?.freight_aed),
              clearing: n(c.clearing ?? u.pricing?.destination_costs?.clearing),
              yard: n(c.yard ?? u.pricing?.destination_costs?.yard),
            },
          },
        };
      });
      const result = await DB.setAsync(key, updated);
      console.log('[saveAll] result for', key, ':', result?.success);
      if (result && !result.success) {
        failed.push({ key, unitCount: unitIds.length, error: result.error });
      } else {
        succeeded.push(key);
      }
    }
    if (failed.length === 0) {
      setChanges({});
      setSavedFlash(true);
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
      flashTimerRef.current = setTimeout(() => {
        setSavedFlash(false);
        flashTimerRef.current = null;
      }, 2000);
      onSave();
    } else if (succeeded.length > 0) {
      console.error('[saveAll] Partial failure:', failed);
      alert('Some pricing changes failed to save. Please try again.');
      onSave();
    } else {
      console.error('[saveAll] Total failure:', failed);
      alert('Failed to save pricing. Please try again.');
    }
  };

  const changedCount = Object.keys(changes).length;

  /* ---- Column definitions ---- */
  const TOTAL_COLS = 28;
  const INFO_COL_COUNT = 10; // number of frozen info columns

  // Sticky-left cumulative widths for frozen info columns
  const infoColWidths = [80, 70, 80, 50, 120, 80, 50, 45, 55, 40]; // CONT#, MAKE, MODEL, TYPE, CHASSIS, ENGINE, T/M, YEAR, COLOR, QTY
  const infoColLefts = infoColWidths.reduce((acc, w, i) => { acc.push(i === 0 ? 0 : acc[i - 1] + infoColWidths[i - 1]); return acc; }, []);
  const frozenWidth = infoColLefts[infoColLefts.length - 1] + infoColWidths[infoColWidths.length - 1];

  const hdrBase = { padding: '6px 4px', fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap', textAlign: 'center', borderBottom: '2px solid #4a6ea8', borderRight: '1px solid #3a5a8a', color: '#fff', letterSpacing: 0.3 };
  const hdrDarkBlue = { ...hdrBase, background: '#1a3a6b' };
  const hdrJpy = { ...hdrBase, background: '#1e3d5c' };
  const hdrJpyTotal = { ...hdrBase, background: '#1a4a2e', color: '#8bdb9e' };
  const hdrRate = { ...hdrBase, background: '#1a3a5b', color: '#ccc' };
  const hdrAedPort = { ...hdrBase, background: '#1a3a5b' };
  const hdrAedLocal = { ...hdrBase, background: '#2a2a4b' };
  const hdrAedTotal = { ...hdrBase, background: '#2a4a3a', color: '#8bdb9e' };
  const hdrTillYard = { ...hdrBase, background: '#4a3a1a', color: '#e8c870' };

  const cellBase = { padding: '4px 5px', fontSize: 11, fontFamily: 'monospace', borderRight: '1px solid var(--border)', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap', verticalAlign: 'middle' };
  const cellInfo = { ...cellBase, fontSize: 10, background: 'var(--bg2)', color: 'var(--text2)' };
  const cellInfoSticky = (idx) => ({ ...cellInfo, position: 'sticky', left: infoColLefts[idx], zIndex: 2, borderRight: idx === INFO_COL_COUNT - 1 ? '2px solid var(--border)' : '1px solid var(--border)' });
  const cellReadonly = { ...cellBase, textAlign: 'right', color: 'var(--text2)' };
  const cellTotal = { ...cellBase, textAlign: 'right', fontWeight: 700 };

  const renderRow = (u, rowIdx) => {
    const japanTotal = calcJapanTotal(u);
    const totalAedPort = Math.round(japanTotal * JPY_AED * 100) / 100;
    const localExp = Math.round(calcLocalExp(u) * 100) / 100;
    const unitPriceAed = Math.round(calcUnitPriceAed(u) * 100) / 100;
    const tillYard = unitPriceAed; // same value
    const contInfo = containerMap[u.id];
    const rowBg = changes[u.id] ? 'rgba(88,166,255,0.05)' : (rowIdx % 2 === 0 ? 'var(--bg1)' : 'var(--bg2)');
    const stickyBg = changes[u.id] ? '#1a2a3a' : (rowIdx % 2 === 0 ? 'var(--bg1)' : 'var(--bg2)');

    return (
      <tr key={u.id} style={{ background: rowBg }}>
        {/* Info columns — sticky */}
        <td style={{ ...cellInfoSticky(0), background: stickyBg, fontWeight: 600 }}>{contInfo?.name || ''}</td>
        <td style={{ ...cellInfoSticky(1), background: stickyBg }}>{u.make || ''}</td>
        <td style={{ ...cellInfoSticky(2), background: stickyBg }}>{u.model || ''}</td>
        <td style={{ ...cellInfoSticky(3), background: stickyBg, textAlign: 'center', fontSize: 9 }}>{u._type}</td>
        <td style={{ ...cellInfoSticky(4), background: stickyBg, fontSize: 9 }}>{u.chassis || ''}</td>
        <td style={{ ...cellInfoSticky(5), background: stickyBg, fontSize: 9 }}>{u.engineName || u.engine || ''}</td>
        <td style={{ ...cellInfoSticky(6), background: stickyBg, fontSize: 9 }}>{u.transmission || ''}</td>
        <td style={{ ...cellInfoSticky(7), background: stickyBg, textAlign: 'center' }}>{u.year || ''}</td>
        <td style={{ ...cellInfoSticky(8), background: stickyBg }}>{u.color || ''}</td>
        <td style={{ ...cellInfoSticky(9), background: stickyBg, textAlign: 'center' }}>{u.quantity || 1}</td>

        {/* JPY editable columns */}
        <td style={cellBase}><SpreadsheetCell value={getVal(u.id, 'auction', u.costJPY || u.purchasePrice)} onChange={v => setVal(u.id, 'auction', v)} baseCurrency="JPY" jpyAed={JPY_AED} jpyUsd={JPY_USD} style={{ minWidth: 90 }} /></td>
        <td style={{ ...cellReadonly, fontSize: 10 }}>{fmtNum(Number(getVal(u.id, 'auction', u.costJPY || u.purchasePrice)) || 0)}</td>
        <td style={cellBase}><SpreadsheetCell value={getVal(u.id, 'recycle', u.pricing?.japan_costs?.recycle_fee)} onChange={v => setVal(u.id, 'recycle', v)} baseCurrency="JPY" jpyAed={JPY_AED} jpyUsd={JPY_USD} style={{ minWidth: 90 }} /></td>
        <td style={cellBase}><SpreadsheetCell value={getVal(u.id, 'misc', u.pricing?.japan_costs?.misc)} onChange={v => setVal(u.id, 'misc', v)} baseCurrency="JPY" jpyAed={JPY_AED} jpyUsd={JPY_USD} style={{ minWidth: 90 }} /></td>
        <td style={cellBase}><SpreadsheetCell value={getVal(u.id, 'bid', u.pricing?.japan_costs?.bid_fee)} onChange={v => setVal(u.id, 'bid', v)} baseCurrency="JPY" jpyAed={JPY_AED} jpyUsd={JPY_USD} style={{ minWidth: 90 }} /></td>
        <td style={cellBase}><SpreadsheetCell value={getVal(u.id, 'transport', u.pricing?.japan_costs?.transport)} onChange={v => setVal(u.id, 'transport', v)} baseCurrency="JPY" jpyAed={JPY_AED} jpyUsd={JPY_USD} style={{ minWidth: 90 }} /></td>
        <td style={cellBase}><SpreadsheetCell value={getVal(u.id, 'vanning', u.pricing?.japan_costs?.vanning)} onChange={v => setVal(u.id, 'vanning', v)} baseCurrency="JPY" jpyAed={JPY_AED} jpyUsd={JPY_USD} style={{ minWidth: 90 }} /></td>
        <td style={cellBase}><SpreadsheetCell value={getVal(u.id, 'freight', u.pricing?.japan_costs?.freight)} onChange={v => setVal(u.id, 'freight', v)} baseCurrency="JPY" jpyAed={JPY_AED} jpyUsd={JPY_USD} style={{ minWidth: 90 }} /></td>
        <td style={cellBase}><SpreadsheetCell value={getVal(u.id, 'nikko', u.pricing?.japan_costs?.nikko)} onChange={v => setVal(u.id, 'nikko', v)} baseCurrency="JPY" jpyAed={JPY_AED} jpyUsd={JPY_USD} style={{ minWidth: 90 }} /></td>
        {/* TOTAL JPY — calculated */}
        <td style={{ ...cellTotal, color: '#4caf50', background: 'rgba(26,74,46,0.25)' }}>{fmtNum(japanTotal)}</td>

        {/* @ rate */}
        <td style={{ ...cellReadonly, fontSize: 9, color: 'var(--text3)', textAlign: 'center' }}>{JPY_AED.toFixed(4)}</td>

        {/* AED columns */}
        <td style={{ ...cellTotal, background: 'rgba(26,58,91,0.2)' }}>{fmtNum(totalAedPort)}</td>
        <td style={{ ...cellBase, background: 'rgba(42,42,75,0.1)' }}><SpreadsheetCell value={getVal(u.id, 'freightAed', u.pricing?.destination_costs?.freight_aed)} onChange={v => setVal(u.id, 'freightAed', v)} baseCurrency="AED" jpyAed={JPY_AED} jpyUsd={JPY_USD} style={{ minWidth: 90 }} /></td>
        <td style={{ ...cellBase, background: 'rgba(42,42,75,0.1)' }}><SpreadsheetCell value={getVal(u.id, 'clearing', u.pricing?.destination_costs?.clearing)} onChange={v => setVal(u.id, 'clearing', v)} baseCurrency="AED" jpyAed={JPY_AED} jpyUsd={JPY_USD} style={{ minWidth: 90 }} /></td>
        <td style={{ ...cellBase, background: 'rgba(42,42,75,0.1)' }}><SpreadsheetCell value={getVal(u.id, 'yard', u.pricing?.destination_costs?.yard)} onChange={v => setVal(u.id, 'yard', v)} baseCurrency="AED" jpyAed={JPY_AED} jpyUsd={JPY_USD} style={{ minWidth: 90 }} /></td>
        <td style={{ ...cellTotal, background: 'rgba(42,42,75,0.15)', color: 'var(--text1)' }}>{fmtNum(localExp)}</td>
        <td style={{ ...cellTotal, background: 'rgba(42,74,58,0.2)', color: '#8bdb9e' }}>{fmtNum(unitPriceAed)}</td>
        <td style={{ ...cellTotal, background: 'rgba(74,58,26,0.2)', color: '#e8c870' }}>{fmtNum(tillYard)}</td>
      </tr>
    );
  };

  return (
    <div>
      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <select className="inv-filter-select" value={groupBy} onChange={e => setGroupBy(e.target.value)}>
          <option value="container">Group by Container</option>
          <option value="none">All Units (flat)</option>
        </select>
        <select className="inv-filter-select" value={filter} onChange={e => { setFilter(e.target.value); setPage(1); }}>
          <option value="all">All ({allUnits.length})</option>
          <option value="unpriced">Needs Pricing</option>
        </select>
        <select className="inv-filter-select" value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); }}>
          <option value="all">All Types</option>
          <option value="VEH">Vehicles</option>
          <option value="ENG">Engines</option>
          <option value="PRT">Parts</option>
        </select>
        <div className="inv-search-wrap">
          <input className="inv-search" placeholder="Search SKU, chassis, make..." value={searchQ} onChange={e => setSearchQ(e.target.value)} style={{ width: 180 }} />
        </div>
        <span style={{ fontSize: 11, color: 'var(--text3)' }}>{units.length} units</span>
        <button className="btn btn-secondary btn-sm" onClick={toggleFullscreen} title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
          {isFullscreen ? 'Exit' : 'Fullscreen'}
        </button>
        <button className="btn btn-secondary btn-sm" onClick={() => {
          exportCSV(units.map(u => {
            const jt = calcJapanTotal(u);
            const tap = Math.round(jt * JPY_AED * 100) / 100;
            const le = Math.round(calcLocalExp(u) * 100) / 100;
            const upa = Math.round(calcUnitPriceAed(u) * 100) / 100;
            return {
              Container: containerMap[u.id]?.name || '',
              Make: u.make || '', Model: u.model || '', Type: u._type, Chassis: u.chassis || '',
              Engine: u.engineName || u.engine || '', 'T/M': u.transmission || '', Year: u.year || '', Color: u.color || '', Qty: u.quantity || 1,
              'Unit Price JPY': u.costJPY || u.purchasePrice || 0, 'Total JPY': jt,
              'Total AED Port': tap, 'Local Exp AED': le, 'Unit Price AED': upa, 'Till Yard': upa,
            };
          }), `pricing-${new Date().toISOString().slice(0,10)}.csv`);
        }}>Export CSV</button>
        <div style={{ flex: 1 }} />
        {(changedCount > 0 || savedFlash) && (
          <button
            className="btn btn-sm"
            onClick={saveAll}
            style={{
              background: savedFlash ? '#22c55e' : 'var(--green)',
              color: '#fff',
              border: 'none',
              transition: 'background 0.3s ease',
              display: 'flex', alignItems: 'center', gap: 4,
            }}
          >
            <CheckIcon /> {savedFlash ? '✓ Saved' : `Save All (${changedCount} changed)`}
          </button>
        )}
      </div>

      {/* Exchange Rates Panel */}
      <div style={{ padding: '10px 14px', background: 'var(--bg3)', borderRadius: 8, marginBottom: 12, border: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text1)' }}>Exchange Rates</span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn btn-secondary btn-sm" style={{ fontSize: 10 }} onClick={fetchLiveRates} disabled={fetchingRates}>
              {fetchingRates ? 'Fetching...' : 'Fetch Live Rates'}
            </button>
            <button className="btn btn-primary btn-sm" style={{ fontSize: 10 }} onClick={saveRates}>Save Rates</button>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, color: 'var(--text3)' }}>JPY - AED:</span>
            <input type="number" step="0.0001" value={jpyAed} onChange={e => { setJpyAed(e.target.value); setRateSource('manual'); }}
              style={{ width: 90, padding: '3px 6px', fontSize: 12, fontFamily: 'monospace', textAlign: 'right', background: 'var(--bg1)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text)' }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, color: 'var(--text3)' }}>JPY - USD:</span>
            <input type="number" step="0.0001" value={jpyUsd} onChange={e => { setJpyUsd(e.target.value); setRateSource('manual'); }}
              style={{ width: 90, padding: '3px 6px', fontSize: 12, fontFamily: 'monospace', textAlign: 'right', background: 'var(--bg1)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text)' }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, color: 'var(--text3)' }}>USD - AED:</span>
            <span style={{ fontSize: 12, fontFamily: 'monospace', color: 'var(--text2)' }}>{(JPY_AED / JPY_USD).toFixed(4)}</span>
          </div>
          <span style={{ fontSize: 9, color: 'var(--text3)' }}>
            {rateUpdated ? `Updated: ${new Date(rateUpdated).toLocaleString()}` : ''} | Source: {rateSource}
          </span>
        </div>
      </div>

      <div style={{ overflowX: 'auto', width: '100%', position: 'relative' }}>
        <table className="data-table" style={{ fontSize: 11, minWidth: 2800, borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <colgroup>
            {/* Info cols */}
            {infoColWidths.map((w, i) => <col key={`info-${i}`} style={{ width: w }} />)}
            {/* JPY cols: UNIT PRICE, TOTAL(display), RECYCLE, MISC, BID FEE, TRANSPORT, VANNING, FREIGHT, COMPANY COMM, TOTAL JPY */}
            <col style={{ width: 100 }} /><col style={{ width: 90 }} /><col style={{ width: 90 }} /><col style={{ width: 90 }} /><col style={{ width: 90 }} /><col style={{ width: 100 }} /><col style={{ width: 90 }} /><col style={{ width: 90 }} /><col style={{ width: 100 }} /><col style={{ width: 110 }} />
            {/* @ rate */}
            <col style={{ width: 60 }} />
            {/* AED cols: TOTAL AED PORT, FREIGHT AED, CLEARING, YARD, LOCAL EXP, UNIT PRICE AED, TILL YARD */}
            <col style={{ width: 100 }} /><col style={{ width: 100 }} /><col style={{ width: 100 }} /><col style={{ width: 90 }} /><col style={{ width: 100 }} /><col style={{ width: 110 }} /><col style={{ width: 110 }} />
          </colgroup>
          <thead>
            <tr style={{ position: 'sticky', top: 0, zIndex: 10 }}>
              {/* Info headers — sticky left */}
              {['CONT#', 'MAKE', 'MODEL', 'TYPE', 'CHASSIS', 'ENGINE', 'T/M', 'YEAR', 'COLOR', 'QTY'].map((label, idx) => (
                <th key={label} style={{ ...hdrDarkBlue, position: 'sticky', left: infoColLefts[idx], zIndex: 11, borderRight: idx === INFO_COL_COUNT - 1 ? '2px solid #4a6ea8' : '1px solid #3a5a8a', minWidth: infoColWidths[idx] }}>{label}</th>
              ))}
              {/* JPY headers */}
              <th style={{ ...hdrJpy, minWidth: 100 }}>UNIT PRICE (JPY)</th>
              <th style={{ ...hdrJpy, minWidth: 90 }}>TOTAL (JPY)</th>
              <th style={{ ...hdrJpy, minWidth: 90 }}>RECYCLE</th>
              <th style={{ ...hdrJpy, minWidth: 90 }}>MISC</th>
              <th style={{ ...hdrJpy, minWidth: 90 }}>BID FEE</th>
              <th style={{ ...hdrJpy, minWidth: 100 }}>TRANSPORT</th>
              <th style={{ ...hdrJpy, minWidth: 90 }}>VANNING</th>
              <th style={{ ...hdrJpy, minWidth: 90 }}>FREIGHT</th>
              <th style={{ ...hdrJpy, minWidth: 100 }}>COMPANY COMM</th>
              <th style={{ ...hdrJpyTotal, minWidth: 110 }}>TOTAL (JPY)</th>
              {/* Rate */}
              <th style={{ ...hdrRate, minWidth: 60 }}>@</th>
              {/* AED headers */}
              <th style={{ ...hdrAedPort, minWidth: 100 }}>TOTAL (AED) PORT</th>
              <th style={{ ...hdrAedLocal, minWidth: 100 }}>FREIGHT (AED)</th>
              <th style={{ ...hdrAedLocal, minWidth: 100 }}>CLEARING EXP</th>
              <th style={{ ...hdrAedLocal, minWidth: 90 }}>YARD (AED)</th>
              <th style={{ ...hdrAedLocal, minWidth: 100 }}>LOCAL EXP</th>
              <th style={{ ...hdrAedTotal, minWidth: 110 }}>UNIT PRICE (AED)</th>
              <th style={{ ...hdrTillYard, minWidth: 110 }}>TILL YARD COST</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              let rowCounter = 0;
              if (groupBy === 'container') {
                const grouped = {};
                const noContainer = [];
                units.forEach(u => {
                  const contInfo = containerMap[u.id];
                  if (contInfo?.name) {
                    if (!grouped[contInfo.name]) grouped[contInfo.name] = { name: contInfo.name, units: [] };
                    grouped[contInfo.name].units.push(u);
                  } else {
                    noContainer.push(u);
                  }
                });
                const groups = [...Object.values(grouped).sort((a, b) => b.units.length - a.units.length)];
                if (noContainer.length > 0) groups.push({ name: 'No Container', units: noContainer });

                return groups.map(g => (
                  <React.Fragment key={g.name}>
                    <tr style={{ background: 'var(--bg3)' }}>
                      <td colSpan={TOTAL_COLS} style={{ fontWeight: 700, fontSize: 11, padding: '8px 10px', color: 'var(--blue)', position: 'sticky', left: 0, borderBottom: '2px solid var(--border)' }}>
                        {g.name} <span style={{ fontWeight: 400, color: 'var(--text3)', marginLeft: 8 }}>{g.units.length} units</span>
                      </td>
                    </tr>
                    {g.units.map(u => { const row = renderRow(u, rowCounter); rowCounter++; return row; })}
                  </React.Fragment>
                ));
              }
              return pagedUnits.map((u, idx) => renderRow(u, idx));
            })()}
          </tbody>
        </table>
      </div>

      {/* Pagination (only for flat view) */}
      {groupBy === 'none' && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
          <span style={{ fontSize: 11, color: 'var(--text3)' }}>{(safePage-1)*perPage+1} - {Math.min(safePage*perPage, units.length)} of {units.length}</span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn btn-secondary btn-sm" disabled={safePage <= 1} onClick={() => setPage(p => p - 1)}>Prev</button>
            <span style={{ fontSize: 11, color: 'var(--text2)', alignSelf: 'center' }}>Page {safePage}/{totalPages}</span>
            <button className="btn btn-secondary btn-sm" disabled={safePage >= totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Pricing;
